import { useEffect, useMemo, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, Sparkles, RotateCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { clientApi, formatCOP } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useSession } from "../context/SessionContext";
import { toast } from "sonner";

const SESSION_STORAGE_KEY = "cm_chat_session_id";

/**
 * Floating AI chatbot widget.
 * - Persists `session_id` in localStorage so conversation memory survives refresh.
 * - Passes the current cart snapshot on every request so the LLM can reason
 *   about "lo que ya tienes" and issue accurate cart_actions.
 * - Executes returned cart_actions locally (against CartContext) and can
 *   navigate to checkout when the assistant confirms the order.
 */
export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role, text, products?}
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(
    () => localStorage.getItem(SESSION_STORAGE_KEY) || ""
  );
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const cart = useCart();
  const { customer } = useSession();

  // Personalized greeting on first open
  useEffect(() => {
    if (!open || messages.length > 0) return;
    let cancel = false;
    clientApi
      .get("/portal/chat/greeting")
      .then(({ data }) => {
        if (cancel) return;
        setMessages([{ role: "assistant", text: data.greeting || "¡Hola! ¿En qué te puedo ayudar?" }]);
      })
      .catch(() => {
        if (!cancel) setMessages([{ role: "assistant", text: "¡Hola! ¿En qué te puedo ayudar hoy?" }]);
      });
    return () => { cancel = true; };
  }, [open, messages.length]);

  // Auto-scroll to bottom on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // Focus the input when the widget opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const cartSnapshot = useMemo(
    () => cart.items.map((i) => ({
      codigo: i.codigo,
      cantidad: i.cantidad,
      descripcion: i.descripcion,
    })),
    [cart.items]
  );

  const applyActions = (actions, productsShown) => {
    if (!actions || actions.length === 0) return;
    // Build a code → product lookup from the products the LLM just showed us,
    // so add() can push a full product shape into the cart with correct price/IVA.
    const lookup = {};
    (productsShown || []).forEach((p) => { lookup[p.codigo] = p; });

    for (const act of actions) {
      const code = act.codigo;
      switch (act.action) {
        case "add": {
          const qty = Math.max(1, Number(act.cantidad) || 1);
          const prod = lookup[code];
          if (!prod) {
            toast.error(`No pude agregar ${code}: producto no está en el catálogo activo.`);
            break;
          }
          cart.add(prod, qty);
          break;
        }
        case "set_qty": {
          const qty = Math.max(0, Number(act.cantidad) || 0);
          if (qty === 0) {
            cart.remove(code);
          } else if (cart.items.some((i) => i.codigo === code)) {
            cart.setQty(code, qty);
          } else {
            // If not in cart yet, treat as add
            const prod = lookup[code];
            if (prod) cart.add(prod, qty);
          }
          break;
        }
        case "remove":
          cart.remove(code);
          break;
        case "clear":
          cart.clear();
          break;
        default:
          break;
      }
    }
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    const nextMsgs = [...messages, { role: "user", text }];
    setMessages(nextMsgs);

    try {
      const { data } = await clientApi.post("/portal/chat/message", {
        session_id: sessionId || undefined,
        message: text,
        cart: cartSnapshot,
      });
      // Persist session id
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem(SESSION_STORAGE_KEY, data.session_id);
      }
      // Execute cart actions before rendering, so the user sees the toast/counter update
      applyActions(data.cart_actions || [], data.highlighted_products || []);

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: data.reply,
          products: data.highlighted_products || [],
          actions: data.cart_actions || [],
        },
      ]);

      if ((data.cart_actions || []).length > 0) {
        const added = data.cart_actions.filter((a) => a.action === "add").length;
        if (added > 0) toast.success(`Asistente actualizó tu carrito (${added} producto${added > 1 ? "s" : ""})`);
      }

      if (data.navigate === "checkout") {
        // Give the user a moment to read the confirmation before navigating
        setTimeout(() => {
          setOpen(false);
          navigate("/checkout");
        }, 1200);
      }
    } catch (err) {
      toast.error("No pude contactar al asistente. Intenta de nuevo.");
      setMessages((prev) => [
        ...prev,
        { role: "assistant", text: "Ups, tuve un problema al procesar tu mensaje. ¿Puedes intentar de nuevo?" },
      ]);
    } finally {
      setSending(false);
    }
  };

  const resetChat = async () => {
    if (!sessionId) {
      setMessages([]);
      return;
    }
    try {
      await clientApi.delete(`/portal/chat/session/${sessionId}`);
    } catch { /* ignore */ }
    localStorage.removeItem(SESSION_STORAGE_KEY);
    setSessionId("");
    setMessages([]);
    toast.success("Conversación reiniciada");
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!customer) return null;

  return (
    <>
      {/* Floating trigger button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-widget-open-btn"
          className="fixed bottom-5 right-5 z-40 group flex items-center gap-2 rounded-full bg-cm-red text-white px-4 py-3 shadow-lg hover:shadow-xl hover:bg-cm-red/90 transition-all"
          aria-label="Abrir asistente de compras"
        >
          <div className="relative">
            <MessageCircle className="h-5 w-5" />
            <Sparkles className="absolute -top-1 -right-1 h-3 w-3 text-cm-yellow animate-pulse" />
          </div>
          <span className="hidden sm:inline text-sm font-medium">Asistente IA</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-5 right-5 z-50 w-[calc(100vw-2.5rem)] sm:w-[380px] h-[75vh] sm:h-[560px] bg-white border border-cm-border rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="bg-cm-red text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="relative">
                <MessageCircle className="h-5 w-5" />
                <Sparkles className="absolute -top-1 -right-1 h-3 w-3 text-cm-yellow" />
              </div>
              <div>
                <p className="text-sm font-medium leading-tight">Asistente CM</p>
                <p className="text-[10px] opacity-80 leading-tight">IA · Claude Sonnet</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={resetChat}
                data-testid="chat-reset-btn"
                className="p-1.5 rounded hover:bg-white/15 transition-colors"
                title="Reiniciar conversación"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
              <button
                onClick={() => setOpen(false)}
                data-testid="chat-widget-close-btn"
                className="p-1.5 rounded hover:bg-white/15 transition-colors"
                title="Cerrar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-cm-cream-alt/40"
            data-testid="chat-messages"
          >
            {messages.map((m, idx) => (
              <MessageBubble key={idx} msg={m} />
            ))}
            {sending && (
              <div className="flex items-center gap-2 text-xs text-cm-ink-soft ml-1" data-testid="chat-typing">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                <span>El asistente está pensando…</span>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-cm-border p-2 flex-shrink-0">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder="Escribe: '5 panelas', 'muéstrame galletas'…"
                rows={1}
                data-testid="chat-input"
                disabled={sending}
                className="flex-1 resize-none border border-cm-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-cm-red max-h-24"
              />
              <button
                onClick={sendMessage}
                disabled={sending || !input.trim()}
                data-testid="chat-send-btn"
                className="bg-cm-red text-white p-2 rounded-lg hover:bg-cm-red/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                aria-label="Enviar"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
            <p className="text-[10px] text-cm-ink-soft mt-1 px-1">
              Las respuestas se basan en tu lista de precios y stock actual.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

const MessageBubble = ({ msg }) => {
  const cart = useCart();
  const isUser = msg.role === "user";

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
          isUser
            ? "bg-cm-red text-white rounded-br-sm"
            : "bg-white border border-cm-border text-cm-ink rounded-bl-sm"
        }`}
        data-testid={isUser ? "chat-msg-user" : "chat-msg-assistant"}
      >
        {msg.text}
        {!isUser && Array.isArray(msg.products) && msg.products.length > 0 && (
          <div className="mt-2 flex flex-col gap-1.5">
            {msg.products.slice(0, 10).map((p) => (
              <div
                key={p.codigo}
                className="bg-cm-cream-alt/60 border border-cm-border rounded-lg p-2 flex items-center gap-2"
                data-testid={`chat-product-${p.codigo}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">{p.nombre}</p>
                  <p className="text-[10px] text-cm-ink-soft truncate">
                    {p.marca || "-"} · Cód {p.codigo} · Stock {p.inventario}
                  </p>
                  <p className="text-xs font-mono text-cm-red mt-0.5">{formatCOP(p.precio)}</p>
                </div>
                {p.disponible && (
                  <button
                    onClick={() => {
                      cart.add(p, 1);
                      toast.success(`+1 ${p.nombre.slice(0, 30)}`);
                    }}
                    className="text-[10px] font-medium px-2 py-1 rounded bg-cm-ink text-white hover:bg-cm-ink/85 flex-shrink-0"
                    data-testid={`chat-quick-add-${p.codigo}`}
                  >
                    + Añadir
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
